import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './GestionDesReservations.css';

const GestionDesReservations = () => {
  const [checkInDate, setCheckInDate] = useState('');
  const [checkOutDate, setCheckOutDate] = useState('');
  const [adults, setAdults] = useState(1);
  const [children, setChildren] = useState(0);
  const [availableRooms, setAvailableRooms] = useState([]);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [reservations, setReservations] = useState([]);
  const [editingReservation, setEditingReservation] = useState(null);

  useEffect(() => {
    fetchReservations();
    if (checkInDate && checkOutDate && checkOutDate > checkInDate) {
      fetchAvailableRooms();
    }
  }, [checkInDate, checkOutDate]);

  const fetchReservations = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/reservations`);
      setReservations(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement des réservations:', error);
    }
  };

  const fetchAvailableRooms = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/rooms/available`, {
        params: { checkInDate, checkOutDate, adults, children }
      });
      setAvailableRooms(response.data.chambres || []);
    } catch (error) {
      console.error('Erreur lors du chargement des chambres:', error);
      setAvailableRooms([]);
    }
  };

  const handleCreateReservation = async (reservationData) => {
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/reservations/createReservation`, {
        ...reservationData,
        
      }, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (response.status === 201) {
        setSelectedRoom(null);
        fetchReservations();
        return response.data;
      } else {
        throw new Error(`Erreur serveur: ${response.status}`);
      }
    } catch (error) {
      console.error('Erreur détaillée:', error.response?.data || error.message);
      alert(`Erreur lors de la création: ${error.response?.data?.message || error.message}`);
      throw error;
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Voulez-vous vraiment supprimer cette réservation ?")) return;
    
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/api/reservations/${id}`);
      setReservations(reservations.filter((res) => res._id !== id));
    } catch (error) {
      console.error("Erreur lors de la suppression :", error);
    }
  };

  const handleEdit = (reservation) => {
    setEditingReservation(reservation);
  };

  const handleUpdateReservation = async () => {
    try {
      await axios.put(`${process.env.REACT_APP_API_URL}/api/reservations/${editingReservation._id}`, editingReservation);
      setReservations(reservations.map((res) => 
        res._id === editingReservation._id ? editingReservation : res
      ));
      setEditingReservation(null);
    } catch (error) {
      console.error("Erreur lors de la modification :", error);
    }
  };

  return (
    <div className="admin-container">
      <header className="admin-header">
        <h1 className="admin-title">Gestion des Réservations</h1>
      </header>

      <div className="control-bar">
        <div className="control-group">
          <label className="control-label">Date d'arrivée</label>
          <input 
            className="control-input"
            type="date" 
            value={checkInDate} 
            min={new Date().toISOString().split('T')[0]} 
            onChange={(e) => setCheckInDate(e.target.value)} 
          />
        </div>
        <div className="control-group">
          <label className="control-label">Date de départ</label>
          <input 
            className="control-input"
            type="date" 
            value={checkOutDate} 
            min={checkInDate} 
            onChange={(e) => setCheckOutDate(e.target.value)} 
          />
        </div>
        <div className="control-group">
          <label className="control-label">Adultes</label>
          <input 
            className="control-input"
            type="number" 
            value={adults} 
            min="1" 
            onChange={(e) => setAdults(e.target.value)} 
          />
        </div>
        <div className="control-group">
          <label className="control-label">Enfants</label>
          <input 
            className="control-input"
            type="number" 
            value={children} 
            min="0" 
            onChange={(e) => setChildren(e.target.value)} 
          />
        </div>
      </div>

      <div className="data-container">
        <div className="data-header">
          <h2 className="data-title">Chambres Disponibles</h2>
        </div>
        <div className="data-cards">
          {availableRooms.length > 0 ? (
            availableRooms.map((room) => (
              <div key={room._id} className="data-card">
                <div className="card-header">
                  <h3 className="card-title">{room.type}</h3>
                  <div className="card-actions">
                    <button 
                      className="btn btn-primary btn-sm"
                      onClick={() => setSelectedRoom(room)}
                    >
                      Réserver
                    </button>
                  </div>
                </div>
                <p>Numéro: {room.number}</p>
                <p>Capacité: {room.capacity} personnes</p>
                <p>Prix: {room.price}€/nuit</p>
              </div>
            ))
          ) : (
            <p>Aucune chambre disponible pour les dates sélectionnées.</p>
          )}
        </div>
      </div>

      {selectedRoom && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3 className="modal-title">Nouvelle Réservation</h3>
              <button 
                className="modal-close"
                onClick={() => setSelectedRoom(null)}
              >
                &times;
              </button>
            </div>
            <div className="modal-body">
              <form onSubmit={(e) => {
                e.preventDefault();
                handleCreateReservation({
                  id_chambre: { number: selectedRoom.number, type: selectedRoom.type },
                  date_entree: checkInDate,
                  date_sortie: checkOutDate,
                  nombre_adultes: adults,
                  nombre_enfants: children,
                  client: {
                    nom: e.target.nom.value,
                    prenom: e.target.prenom.value,
                    email: e.target.email.value,
                    telephone: e.target.telephone.value
                  }
                });
              }}>
                <div className="form-group">
                  <label className="form-label">Nom</label>
                  <input 
                    className="form-control"
                    type="text" 
                    name="nom" 
                    required 
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Prénom</label>
                  <input 
                    className="form-control"
                    type="text" 
                    name="prenom" 
                    required 
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Email</label>
                  <input 
                    className="form-control"
                    type="email" 
                    name="email" 
                    required 
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Téléphone</label>
                  <input 
                    className="form-control"
                    type="tel" 
                    name="telephone" 
                    required 
                  />
                </div>
                <div className="modal-footer">
                  <button 
                    type="button" 
                    className="btn btn-secondary"
                    onClick={() => setSelectedRoom(null)}
                  >
                    Annuler
                  </button>
                  <button 
                    type="submit" 
                    className="btn btn-primary"
                  >
                    Confirmer
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      <div className="data-container">
        <div className="data-header">
          <h2 className="data-title">Liste des Réservations</h2>
        </div>
        <table className="data-table">
          <thead>
            <tr>
              <th>Client</th>
              <th>Chambre</th>
              <th>Dates</th>
              <th>Personnes</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {reservations.length > 0 ? (
              reservations.map((res) => (
                <tr key={res._id}>
                  <td>
                    <strong>{res.client.nom} {res.client.prenom}</strong>
                    <div className="text-muted">{res.client.email}</div>
                  </td>
                  <td>
                    {res.id_chambre?.number} ({res.id_chambre?.type})
                  </td>
                  <td>
                    {new Date(res.date_entree).toLocaleDateString()} - {' '}
                    {new Date(res.date_sortie).toLocaleDateString()}
                  </td>
                  <td>
                    {res.nombre_adultes} adultes, {res.nombre_enfants} enfants
                  </td>
                  <td>
                    <div className="data-actions">
                      <button 
                        className="btn btn-warning btn-sm"
                        onClick={() => handleEdit(res)}
                      >
                        Modifier
                      </button>
                      <button 
                        className="btn btn-danger btn-sm"
                        onClick={() => handleDelete(res._id)}
                      >
                        Supprimer
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="5" style={{ textAlign: 'center' }}>
                  Aucune réservation trouvée.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {editingReservation && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3 className="modal-title">Modifier Réservation</h3>
              <button 
                className="modal-close"
                onClick={() => setEditingReservation(null)}
              >
                &times;
              </button>
            </div>
            <div className="modal-body">
              <form onSubmit={(e) => {
                e.preventDefault();
                handleUpdateReservation();
              }}>
                <div className="form-group">
                  <label className="form-label">Nom</label>
                  <input 
                    className="form-control"
                    type="text" 
                    value={editingReservation.client.nom} 
                    onChange={(e) => setEditingReservation({
                      ...editingReservation,
                      client: { ...editingReservation.client, nom: e.target.value }
                    })} 
                    required 
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Prénom</label>
                  <input 
                    className="form-control"
                    type="text" 
                    value={editingReservation.client.prenom} 
                    onChange={(e) => setEditingReservation({
                      ...editingReservation,
                      client: { ...editingReservation.client, prenom: e.target.value }
                    })} 
                    required 
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Email</label>
                  <input 
                    className="form-control"
                    type="email" 
                    value={editingReservation.client.email} 
                    onChange={(e) => setEditingReservation({
                      ...editingReservation,
                      client: { ...editingReservation.client, email: e.target.value }
                    })} 
                    required 
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Téléphone</label>
                  <input 
                    className="form-control"
                    type="tel" 
                    value={editingReservation.client.telephone} 
                    onChange={(e) => setEditingReservation({
                      ...editingReservation,
                      client: { ...editingReservation.client, telephone: e.target.value }
                    })} 
                    required 
                  />
                </div>
                <div className="modal-footer">
                  <button 
                    type="button" 
                    className="btn btn-secondary"
                    onClick={() => setEditingReservation(null)}
                  >
                    Annuler
                  </button>
                  <button 
                    type="submit" 
                    className="btn btn-primary"
                  >
                    Sauvegarder
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GestionDesReservations;